/**
 * 機能フラグ制御フック
 *
 * 組織ごとの機能有効/無効を制御する。
 * 現時点ではハードコードされたデフォルト値を返す。
 * 将来的に Settings API から feature flags を取得予定。
 */
import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';

// ==============================
// 型定義
// ==============================

/**
 * 機能フラグ名
 *
 * ここに載るのは「bundle 内の定数で決まる」フラグだけ。組織ごとに出し分けるフラグは
 * ここに置かない (置くと bundle を作り直さない限り開放できなくなる)。
 *
 * `bulk_url_send` は **意図的に含めない**。資料回収URL 一括送信は組織単位の
 * canonical settings API を正本とし、`useBulkUrlSendFeature` から取得する。
 * ここへ再追加すると hardcode 値が runtime authority に戻ってしまうため、
 * 型レベルで参照できないようにしている (`pages/bulkUrlSendIsolation.source.test.ts` で固定)。
 */
export type FeatureFlag = 'chatwork' | 'line' | 'vault_auto_save' | 'portal_messages';

// ==============================
// デフォルト値
// ==============================

/**
 * デフォルトの機能フラグ
 *
 * - chatwork: リリース済み (有効)
 * - line: リリース済み (有効)
 * - vault_auto_save: 未リリース (無効)
 * - portal_messages: リリース済み (有効)
 *
 * `bulk_url_send` はここに置かない。組織単位の settings API を正本とし
 * (`useBulkUrlSendFeature`)、bundle 再作成なしで開放できるようにしている。
 */
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  chatwork: true,
  line: true,
  vault_auto_save: false,
  portal_messages: true,
};

// ==============================
// useFeatureGate フック
// ==============================

interface UseFeatureGateReturn {
  /** 指定した機能フラグが有効かどうかを判定する */
  isEnabled(feature: FeatureFlag): boolean;
}

/**
 * 機能フラグの有効/無効を判定するフック
 *
 * 未認証状態では全て無効を返す。
 * 将来的に Settings API 連携時は組織ごとの設定を取得する。
 */
export function useFeatureGate(): UseFeatureGateReturn {
  const { user } = useAuth();

  const result = useMemo<UseFeatureGateReturn>(() => {
    // 未認証の場合は全て無効
    if (!user) {
      return {
        isEnabled: () => false,
      };
    }

    // デフォルト値を使用 (将来: Settings API から組織ごとの feature flags を取得)
    const flags = { ...DEFAULT_FLAGS };

    return {
      isEnabled: (feature: FeatureFlag) => flags[feature] ?? false,
    };
  }, [user]);

  return result;
}
